import { EColorState, IPaymentCard, IPaymentCardActions, IPaymentCardListItem, PaymentCardIconSize, PaymentCardVariant } from '@/components/IBPayment/interface';
import IBAccountFormatter from '@/mixins/account/IBAccountFormatter';
import DateOperations from '@/mixins/date/date-operations';
import TimerUtils from '@/modules/Timer/TimerUtils';
import { Component, Mixins } from 'vue-property-decorator';
import { PaymentRequirementsHistoryItem } from '../../../api/history/payment-requirements-history.interface';
import { PaymentRequirementsIncomingStatus, PaymentRequirementsItem } from '../../../api/incoming/payment-requirements-incoming.interface';
import { PaymentRequirementsCardContentAndStatusData, PaymentRequirementsStatusData } from '../interface';
import PaymentRequirementsCardActions from '../History/PaymentRequirementsCardActions';
import PaymentRequirementsStatusMapper from '../Status/PaymentRequirementsStatusMapper';

@Component
export default class PaymentRequirementsMapper extends Mixins(
	DateOperations,
	IBAccountFormatter,
	PaymentRequirementsStatusMapper,
	PaymentRequirementsCardActions
) {

	paymentRequirementsList?: PaymentRequirementsItem[]

	paymentRequirementsLoading?: boolean

	showPaymentRequirementsStatusesInCardHeader?: boolean

	serverTime?: number

	paymentRequirementsDueDaysError: number = 2

	mapPaymentRequirementsItemToCardContentList(
		paymentRequirementsItem: PaymentRequirementsHistoryItem
	): IPaymentCardListItem[] {
		const list: IPaymentCardListItem[] = [
			{
				title: this.$t('cardIndex.docNumber') as string,
				text: paymentRequirementsItem.documentNumber || '-'
			},
			{
				title: this.$t('paymentRequirements.docDate') as string,
				text: paymentRequirementsItem.documentDate || '-'
			},
			{
				title: this.$t('paymentRequirements.debtType') as string,
				text: (paymentRequirementsItem.paymentType || '') +
					' ' +
					(paymentRequirementsItem.paymentTypeName || '')
			},
			{
				title: this.$t('paymentRequirements.destCardIndexCode') as string,
				text: (paymentRequirementsItem.unpaid || '') +
					' ' +
					(paymentRequirementsItem.unpaidName || '')
			},
			{
				title: this.$t('paymentRequirements.destCode') as string,
				text: (paymentRequirementsItem.purposeCode || '') +
					' ' +
					(paymentRequirementsItem.purposeCodeName || '')
			},
			...(paymentRequirementsItem.income
				? this.getPaymentRequirementsIncomingCardList(
					paymentRequirementsItem
				)
				: this.getPaymentRequirementsOutgoingCardList(
					paymentRequirementsItem
				))
		];

		if (paymentRequirementsItem.dueDate
			&& paymentRequirementsItem.status === PaymentRequirementsIncomingStatus.CAPITALIZED_K1
			&& paymentRequirementsItem.income) {
			list.unshift({
				title: this.$t('paymentRequirements.dueDate') as string,
				text: paymentRequirementsItem.dueDate,
				textColor: this.getPaymentRequirementsDueDateColor(
					paymentRequirementsItem.dueDate
				)
			});
		}

		return list;
	}

	getPaymentRequirementsOutgoingCardList(
		paymentRequirementsItem: PaymentRequirementsHistoryItem
	): IPaymentCardListItem[] {
		return [
			{
				heading: this.$t('paymentRequirements.fromCompany') as string
			},
			{
				title: this.$t('electCommerce.name') as string,
				text: paymentRequirementsItem.debitName || '-'
			},
			{
				title: paymentRequirementsItem.debitInn
					? this.$t('createSumPayment.recipient.textField3') as string
					: this.$t('paymentRequirements.pinfl') as string,
				text: paymentRequirementsItem.debitInn
					|| paymentRequirementsItem.debitPinfl
			},
			{
				title: this.$t('companyAccounts.bills.info.number') as string,
				text: this.formatAccount(
					paymentRequirementsItem.debitAccountNumber || '-'
				)
			},
			{
				title: this.$t('companyAccounts.bills.info.mfo') as string,
				text: paymentRequirementsItem.debitMfo || '-'
			},
			{
				title: this.$t('createSumPayment.nameBank') as string,
				text: paymentRequirementsItem.debitMfoName || '-'
			},
		];
	}
	
	getPaymentRequirementsIncomingCardList(
		paymentRequirementsItem: PaymentRequirementsHistoryItem
	): IPaymentCardListItem[] {
		return [
			{
				heading: this.$t('paymentRequirements.toCompany') as string
			},
			{
				title: this.$t('electCommerce.name') as string,
				text: paymentRequirementsItem.requesterName || '-'
			},
			{
				title: paymentRequirementsItem.requesterInn
					? this.$t('createSumPayment.recipient.textField3') as string
					: this.$t('paymentRequirements.pinfl') as string,
				text: paymentRequirementsItem.requesterInn
					|| paymentRequirementsItem.requesterPinfl
			},
			{
				title: this.$t('companyAccounts.bills.info.number') as string,
				text: this.formatAccount(
					paymentRequirementsItem.requesterAccountNumber || '-'
				)
			},
			{
				title: this.$t('companyAccounts.bills.info.mfo') as string,
				text: paymentRequirementsItem.requesterMfo || '-'
			},
			{
				title: this.$t('createSumPayment.nameBank') as string,
				text: paymentRequirementsItem.requesterMfoName || '-'
			},
		];
	}

	getPaymentRequirementsDueDateColor(
		dueDate: string
	): EColorState {
		if (!this.serverTime) return EColorState.default;

		const dueDateInMs = this.getTimestampByFormattedDate(dueDate);

		if (this.serverTime >= dueDateInMs) return EColorState.danger;

		const daysLeft = this.getPaymentRequirementsDueDays(dueDate);

		return daysLeft >= this.paymentRequirementsDueDaysError
			? EColorState.warning
			: EColorState.danger;
	}

	getPaymentRequirementsDueDays(
		dueDate: string
	): number {
		if (!this.serverTime) return 0;

		const dueDateInMs = this.getTimestampByFormattedDate(dueDate);

		const timeLeftInMs = Math.abs(
			this.serverTime >= dueDateInMs ? 0 : this.serverTime - dueDateInMs
		);

		return Math.ceil(timeLeftInMs / TimerUtils.MS_IN_DAY);
	}

	getPaymentRequirementsStatusData(
		paymentRequirementsItem: PaymentRequirementsItem
	): PaymentRequirementsStatusData {
		const statusConfig = this.getPaymentRequirementsStatusConfig(
			paymentRequirementsItem
		);

		return {
			statusConfig,
			statusText: paymentRequirementsItem.statusName || '-',
			statusColor: statusConfig?.color || EColorState.info
		};
	}

	getPaymentRequirementsCardContentAndStatusData(
		paymentRequirementsItem: PaymentRequirementsItem
	): PaymentRequirementsCardContentAndStatusData {
		const statusData = this.getPaymentRequirementsStatusData(
			paymentRequirementsItem
		);

		return {
			statusData,
			content: {
				chips: [
					{ text: statusData.statusText, type: statusData.statusColor }
				],
				label: {
					title: this.$t('payroll.newPayroll.purposePay') as string,
					text: paymentRequirementsItem.purpose,
				},
				icon: statusData.statusConfig?.iconType
					? { type: statusData.statusConfig?.iconType }
					: {
						src: '/img/icons/insert-drive-file_bg.svg',
						withoutBg: true,
						size: PaymentCardIconSize.large
					},
				list: this.mapPaymentRequirementsItemToCardContentList(
					paymentRequirementsItem
				),
				loading: false
			}
		};
	}

	getPaymentRequirementsActions(
		paymentCard: IPaymentCard<PaymentRequirementsItem | null>
	): IPaymentCardActions | undefined {
		return {
			right: [
				{
					action: this.deniePaymentRequirementsCard.bind(this, paymentCard),
					color: EColorState.info,
					text: this.$t('paymentRequirements.denie') as string,
				},
				{
					action: this.payPaymentRequirementsCard.bind(this, paymentCard),
					color: EColorState.primary,
					text: this.$t('paymentRequirements.pay') as string,
				}
			]
		};
	}

	mapPaymentRequirementsItemToCard(
		paymentRequirementsItem: PaymentRequirementsItem
	): IPaymentCard {
		const {
			content,
			statusData
		} = this.getPaymentRequirementsCardContentAndStatusData(
			paymentRequirementsItem
		);

		const paymentCard: IPaymentCard<PaymentRequirementsItem> = {
			...(this.showPaymentRequirementsStatusesInCardHeader
				? {
					bottomTitle: statusData.statusText,
					bottomTitleColor: statusData.statusColor,
				}
				: {}),
			errorPayment: this.definePage,
			outgoingPaymentStatus: paymentRequirementsItem.status,
			errorText: paymentRequirementsItem.confirmText,
			dateTime: paymentRequirementsItem.receiveDate
				? this.getTimestampByFormatted(paymentRequirementsItem.receiveDate)
				: Date.now(),
			heading: {
				text: this.definePage ? paymentRequirementsItem.debitName : paymentRequirementsItem.requesterName
			},
			bottomComment: paymentRequirementsItem.purpose,
			amount: (paymentRequirementsItem.amount || 0) / 100,
			currency: 'UZS',
			icon: {
				src: '/img/icons/insert-drive-file.svg'
			},
			showAmountInHead: true,
			content,
			mobile: {
				label: paymentRequirementsItem.purpose
			},
			meta: paymentRequirementsItem
		};

		if (paymentRequirementsItem.status === PaymentRequirementsIncomingStatus.CAPITALIZED_K1) {
			paymentCard.actions = this.getPaymentRequirementsActions(
				paymentCard
			);
		}

		return paymentCard;
	}

	mapPaymentRequirementsListToCards(
		paymentRequirementsList: PaymentRequirementsItem[]
	): IPaymentCard[] {
		return paymentRequirementsList.map(
			this.mapPaymentRequirementsItemToCard.bind(this)
		);
	}

	get definePage(): boolean {
		return !!(this.$route.path === "/payment-requirements/outgoing");
	}

	get PaymentCardVariant(): typeof PaymentCardVariant {
		return PaymentCardVariant;
	}

	get paymentRequirementsCardList(): IPaymentCard[] {
		return this.mapPaymentRequirementsListToCards(
			this.paymentRequirementsList || []
		);
	}

	get paymentRequirementsIsEmpty(): boolean {
		return !this.paymentRequirementsList?.length
			&& !this.paymentRequirementsLoading;
	}

}